import { useEffect, useMemo, useState } from "react";
import { Circle, Group, Label, Line, Tag, Text } from "react-konva";
import { KonvaEventObject } from "konva/lib/Node";
import { clamp, getPolygonLabelAnchor, getPolygonPointBounds, type CanvasPoint } from "./geometry";

type Point = CanvasPoint;

/* Prop Types */
interface PolygonProps {
  id: string;
  points: Point[];
  ratio: number;
  isSelected?: boolean;
  canvasSize: { width: number; height: number };
  label: string;
  labelColor: string;
  backgroundColor: string;
  strokeColor: string;
  setSelectedRegion: (id: string) => void;
  onRegionSave: (id: string, points: Point[]) => void;
  onContextMenu?: (e: KonvaEventObject<PointerEvent>) => void;
  toolMode?: "pan" | "draw";
}

/*
 * Polygon Component
 * Renders editable polygon AOIs and supports dragging vertices or the full polygon.
 */
export const Polygon = ({
  id,
  points,
  ratio,
  isSelected,
  canvasSize,
  label,
  labelColor,
  backgroundColor,
  strokeColor,
  setSelectedRegion,
  onRegionSave,
  onContextMenu,
  toolMode = "pan",
}: PolygonProps) => {
  /* State */
  const [draftPoints, setDraftPoints] = useState<Point[]>(points);
  const [groupOffset, setGroupOffset] = useState({ x: 0, y: 0 });

  /* Keep local editable points in sync with upstream region updates */
  useEffect(() => {
    setDraftPoints(points);
    setGroupOffset({ x: 0, y: 0 });
  }, [points]);

  /* Pre-scale points once for Konva's flat [x1, y1, x2, y2, ...] shape format */
  const scaledPoints = useMemo(() => draftPoints.flatMap((point) => [point.x * ratio, point.y * ratio]), [draftPoints, ratio]);

  /* Used to clamp polygon dragging so no vertex can leave the image bounds */
  const pointBounds = useMemo(() => getPolygonPointBounds(draftPoints, ratio), [draftPoints, ratio]);

  /* Commit whole-polygon drag by applying the temporary group offset to each point */
  const handlePolygonDragEnd = () => {
    // Scale the group offset back down to the original coordinate space before applying to points
    const dx = groupOffset.x / ratio;
    const dy = groupOffset.y / ratio;

    // If there was no movement, do nothing
    if (!dx && !dy) {
      return;
    }

    // Apply the offset to each point to get their new positions
    const nextPoints = draftPoints.map((point) => ({
      x: point.x + dx,
      y: point.y + dy,
    }));

    // Reset group offset and update points with the new positions
    setGroupOffset({ x: 0, y: 0 });
    setDraftPoints(nextPoints);
    onRegionSave(id, nextPoints);
  };

  /* Live-update one vertex while dragging, clamped to the image */
  const handleVertexDragMove = (index: number) => (e: KonvaEventObject<DragEvent>) => {
    e.cancelBubble = true;

    // Get the absolute position of the dragged vertex by adding the current group offset
    const clampedCanvasX = clamp(groupOffset.x + e.target.x(), 0, canvasSize.width);
    const clampedCanvasY = clamp(groupOffset.y + e.target.y(), 0, canvasSize.height);

    // Scale back down to the original coordinate space for saving
    const x = clampedCanvasX / ratio;
    const y = clampedCanvasY / ratio;

    // Move the dragged vertex to the clamped position within the group
    e.target.x(clampedCanvasX - groupOffset.x);
    e.target.y(clampedCanvasY - groupOffset.y);

    // Update the dragged vertex position in state for live feedback
    setDraftPoints((current) => current.map((point, pointIndex) => (pointIndex === index ? { x, y } : point)));
  };

  /* Persist one vertex position when drag finishes */
  const handleVertexDragEnd = (index: number) => (e: KonvaEventObject<DragEvent>) => {
    e.cancelBubble = true;

    // Get the absolute position of the dragged vertex by adding the current group offset
    const clampedCanvasX = clamp(groupOffset.x + e.target.x(), 0, canvasSize.width);
    const clampedCanvasY = clamp(groupOffset.y + e.target.y(), 0, canvasSize.height);

    // Scale back down to the original coordinate space for saving
    const x = clampedCanvasX / ratio;
    const y = clampedCanvasY / ratio;

    setDraftPoints((current) => {
      const nextPoints = current.map((point, pointIndex) => (pointIndex === index ? { x, y } : point));
      onRegionSave(id, nextPoints);
      return nextPoints;
    });
  };

  /* Anchor label near the top-left area: highest point from points on the left side */
  const labelAnchor = getPolygonLabelAnchor(draftPoints);

  /* Clamp group translation while dragging the entire polygon */
  const handleDragMove = (e: KonvaEventObject<Event>) => {
    const newX = clamp(e.target.x(), -pointBounds.minX, canvasSize.width - pointBounds.maxX);
    const newY = clamp(e.target.y(), -pointBounds.minY, canvasSize.height - pointBounds.maxY);

    e.target.x(newX);
    e.target.y(newY);
    setGroupOffset({ x: newX, y: newY });
  };

  /* Return null if there are no points to render */
  if (!points?.length) return null;

  /* Render */
  return (
    <>
      <Group
        x={groupOffset.x}
        y={groupOffset.y}
        draggable={toolMode === "pan" && !!isSelected}
        listening={toolMode === "pan"}
        onClick={toolMode === "pan" ? () => setSelectedRegion(id) : undefined}
        onTap={toolMode === "pan" ? () => setSelectedRegion(id) : undefined}
        onDragMove={(e) => {
          handleDragMove(e);
        }}
        onDragEnd={handlePolygonDragEnd}
        onContextMenu={onContextMenu}
      >
        <Line points={scaledPoints} closed stroke={strokeColor} strokeWidth={2} fill={backgroundColor} hitStrokeWidth={10} />

        <Label x={labelAnchor.x * ratio + 1} y={labelAnchor.y * ratio + 1} onClick={toolMode === "pan" ? () => setSelectedRegion(id) : undefined} listening={toolMode === "pan"}>
          <Tag fill={labelColor} />
          <Text text={label} fontSize={14} fontStyle="bold" padding={6 * ratio} fill="white" fontFamily="Inter" />
        </Label>

        {isSelected &&
          toolMode === "pan" &&
          draftPoints.map((point, index) => (
            <Circle
              key={`${id}-vertex-${index}`}
              x={point.x * ratio}
              y={point.y * ratio}
              radius={5}
              fill="white"
              stroke={strokeColor}
              strokeWidth={2}
              draggable
              onMouseEnter={(e) => {
                // Change cursor to move when hovering over a vertex
                const stage = e.target.getStage();
                if (stage) stage.container().style.cursor = "move";
              }}
              onMouseLeave={(e) => {
                // Revert cursor when not hovering over a vertex
                const stage = e.target.getStage();
                if (stage) stage.container().style.cursor = "";
              }}
              onMouseDown={(e) => {
                e.cancelBubble = true;
              }}
              onTouchStart={(e) => {
                e.cancelBubble = true;
              }}
              onDragMove={handleVertexDragMove(index)}
              onDragEnd={handleVertexDragEnd(index)}
            />
          ))}
      </Group>
    </>
  );
};
